import React from 'react';
import { IResortPathInfo } from 'api/resort/types';
import { ISubLink, ITabLink } from 'components/common/StickyTabsNav/types';
import { Routes } from 'routes';
import { isMainLocale, isSkiRentalsLocale } from 'util/i18nFlags';
import { ReactComponent as SvgSnowFlake } from 'public/assets/icons/snowflake.svg';
import { ReactComponent as SvgMap } from 'public/assets/icons/map.svg';
import { CommonKeyTranslationFuncProps } from 'i18n/useOTSTranslation';
import { hasWeSkiIntegration } from 'components/common/PlanYourTrip/WeSki/resolver';

export enum CurrentTab {
  SNOW = 'snow_reports',
  TRIP_PLANNING = 'trip_planning',
  LODGING = 'lodging',
  // FLIGHTS = 'flights',
  // CAR_RENTALS = 'car-rentals',
  BOOK_TRIP = 'book-trip',
}

export enum CurrentLink {
  SNOW_REPORT = 'snow_reports',
  WEATHER = 'weather',
  CAMS = 'cams',
  FHR = 'first_hand',
  OVERVIEW = 'ski-resort',
  REVIEWS = 'reviews',
  TRAILMAP = 'trailmap',
  SNOWPLANNER = 'snowplanner',
  LIFTACCESS = 'liftaccess',
  LODGING = 'lodging',
  LIFT_TICKETS = 'lift-tickets',
  SKI_RENTALS = 'skirentals',
  BOOK_TRIP = 'book-trip',
  SUMMER_OVERVIEW = 'summer-overview',
}

export const tabs: (resort: IResortPathInfo) => Array<ITabLink<CurrentTab>> = (resort: IResortPathInfo) => (isMainLocale() ? [
  {
    render: (t) => (
      <>
        <SvgSnowFlake height={20} />
        {t('nav.tab.snow')}
      </>
    ),
    uuid: CurrentTab.SNOW,
    href: Routes.resortSnowReport(resort),
  },
  {
    render: (t) => (
      <>
        <SvgMap height={27} />
        {t('nav.tab.tripPlanning')}
      </>
    ),
    uuid: CurrentTab.TRIP_PLANNING,
    href: Routes.resortTripPlanningOverview(resort),
  },
] : [
  {
    render: (t) => (
      <>
        <SvgSnowFlake height={20} />
        {t('nav.tab.snow')}
      </>
    ),
    uuid: CurrentTab.SNOW,
    href: Routes.resortSnowReport(resort),
  },
]);

export const linksSnow: (
  resort: IResortPathInfo,
  t: CommonKeyTranslationFuncProps,
  isPartner?: boolean,
) => Array<ISubLink<CurrentLink>> = (resort: IResortPathInfo, t, isPartner = true) => {
  const links: Array<ISubLink<CurrentLink>> = [
    {
      title: t('nav.tab.overview'),
      isExternal: false,
      href: Routes.resortTripPlanningOverview(resort),
      uuid: CurrentLink.OVERVIEW,
    },
    {
      title: t('nav.tab.snowReport'),
      isExternal: false,
      href: Routes.resortSnowReport(resort),
      uuid: CurrentLink.SNOW_REPORT,
    },
    {
      title: t('nav.tab.weather'),
      isExternal: false,
      href: Routes.resortWeather(resort),
      uuid: CurrentLink.WEATHER,
    },
    {
      title: t('nav.tab.cams'),
      isExternal: false,
      href: Routes.resortCams(resort),
      uuid: CurrentLink.CAMS,
    },
    {
      title: t('nav.tab.fhr'),
      isExternal: false,
      href: Routes.resortFHReports(resort),
      uuid: CurrentLink.FHR,
    },
    {
      title: t('nav.tab.reviews'),
      isExternal: false,
      href: Routes.resortTripPlanningReviews(resort),
      uuid: CurrentLink.REVIEWS,
    },
    {
      title: t('nav.tab.trailMap'),
      isExternal: false,
      href: Routes.resortTripPlanningTrailMap(resort),
      uuid: CurrentLink.TRAILMAP,
    },
    {
      title: t('nav.tab.snowPlanner'),
      isExternal: false,
      href: Routes.resortSnowfall(resort),
      uuid: CurrentLink.SNOWPLANNER,
    },
    {
      title: t('nav.tab.liftTickets'),
      isExternal: false,
      href: Routes.resortLiftTickets(resort),
      uuid: CurrentLink.LIFT_TICKETS,
    },
  ];

  if (isPartner) {
    links.push({
      title: t('nav.tab.summerGuide'),
      isExternal: false,
      href: Routes.resortSummerOverview(resort),
      uuid: CurrentLink.SUMMER_OVERVIEW,
    });
  }

  return links;
};

export const linksPlan: (
  resort: IResortPathInfo,
  liftsUrl: string | undefined,
  t: CommonKeyTranslationFuncProps
) => Array<ISubLink<CurrentLink>> = (resort: IResortPathInfo, liftsUrl, t) => [
  liftsUrl ? {
    title: t('nav.tab.liftAccess'),
    isExternal: true,
    href: liftsUrl,
    uuid: CurrentLink.LIFTACCESS,
  } : undefined as any, // We filter it out later in code
  (!hasWeSkiIntegration({ resortId: resort.uuid })) ? {
    title: t('nav.tab.lodging'),
    isExternal: false,
    href: Routes.resortTripPlanningLodging(resort),
    uuid: CurrentLink.LODGING,
  } : undefined,
  (hasWeSkiIntegration({ resortId: resort.uuid })) ? {
    title: t('nav.tab.SkiHolidays'),
    isExternal: false,
    href: Routes.resortBookTrip(resort),
    uuid: CurrentLink.BOOK_TRIP,
  } : undefined,
  (isSkiRentalsLocale() && resort.hasSkiRental) ? {
    title: t('nav.tab.skiRental'),
    isExternal: false,
    href: Routes.resortSkiRentals(resort),
    uuid: CurrentLink.SKI_RENTALS,
  } : undefined,
].filter((d) => !!d);

export const onlyLodgingAndSkiRental: (
  resort: IResortPathInfo,
  t: CommonKeyTranslationFuncProps
) => Array<ISubLink<CurrentLink>> = (resort: IResortPathInfo, t) => [
  (!hasWeSkiIntegration({ resortId: resort.uuid })) ? {
    title: t('nav.tab.lodging'),
    isExternal: false,
    href: Routes.resortTripPlanningLodging(resort),
    uuid: CurrentLink.LODGING,
  } : undefined,
  (hasWeSkiIntegration({ resortId: resort.uuid })) ? {
    title: t('nav.tab.SkiHolidays'),
    isExternal: false,
    href: Routes.resortBookTrip(resort),
    uuid: CurrentLink.BOOK_TRIP,
  } : undefined,
  (isSkiRentalsLocale() && resort.hasSkiRental) ? {
    title: t('nav.tab.skiRental'),
    isExternal: false,
    href: Routes.resortSkiRentals(resort),
    uuid: CurrentLink.SKI_RENTALS,
  } : undefined,
].filter((d) => !!d) as Array<ISubLink<CurrentLink>>;
